import { jsPDF } from 'jspdf'
import { obterValorColuna } from '@/utils/apresentacaoGestaoEntregas'
import logoRoboflex from '@/assets/img/roboflex/logo-completo-preto.png'

/** Proporção real do arquivo logo-completo-preto.png (666×375 px). */
const LOGO_ASPECT = 666 / 375
const LOGO_ALTURA_MM = 11

const COR_PRIMARIA = [0, 138, 136]
const COR_PRIMARIA_CLARA = [0, 182, 179]
const COR_CABECALHO_FUNDO = [240, 249, 249]
const COR_ZEBRA = [245, 250, 250]
const COR_TEXTO_SECUNDARIO = [90, 90, 90]

const PESOS_COLUNA = {
    pcm_codigo: 1.15,
    projeto_nome: 2.4,
    nome: 2.4,
    entrega_nome: 2.2,
    gerente_nome: 1.7,
    fim_previsto: 1.05,
    entrega_status: 1.15,
    status: 1.15,
    projeto_status: 1.15,
    coordenador_nome: 1.5,
    resultado_ser_alcancado: 2,
    observacao: 1.8,
    default: 1,
}

function nomeArquivoPdf() {
    const d = new Date()
    const dia = String(d.getDate()).padStart(2, '0')
    const mes = String(d.getMonth() + 1).padStart(2, '0')
    const ano = d.getFullYear()
    return `Revisao-Prioridades-${dia}-${mes}-${ano}.pdf`
}

function dimensoesPagina(doc) {
    return {
        largura: doc.internal.pageSize.getWidth(),
        altura: doc.internal.pageSize.getHeight(),
    }
}

function desenharFaixaCabecalho(doc, dims, margemX) {
    doc.setFillColor(...COR_CABECALHO_FUNDO)
    doc.rect(0, 0, dims.largura, 20, 'F')
    doc.setDrawColor(...COR_PRIMARIA_CLARA)
    doc.setLineWidth(0.4)
    doc.line(margemX, 20, dims.largura - margemX, 20)
}

function adicionarLogo(doc, margemX) {
    try {
        const h = LOGO_ALTURA_MM
        const w = h * LOGO_ASPECT
        doc.addImage(logoRoboflex, 'PNG', margemX, 4.5, w, h, undefined, 'SLOW')
    } catch {
        /* logo opcional */
    }
}

function desenharRodape(doc, dims, margemX, pagina, total) {
    const y = dims.altura - 8
    doc.setDrawColor(...COR_PRIMARIA_CLARA)
    doc.setLineWidth(0.2)
    doc.line(margemX, y - 3, dims.largura - margemX, y - 3)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(...COR_TEXTO_SECUNDARIO)
    doc.text(`${pagina} / ${total}`, dims.largura / 2, y, { align: 'center' })
    doc.setTextColor(0, 0, 0)
}

function prepararPagina(doc, dims, margemX, pagina, total) {
    desenharFaixaCabecalho(doc, dims, margemX)
    adicionarLogo(doc, margemX)
    desenharRodape(doc, dims, margemX, pagina, total)
}

function novaPagina(doc, dims, margemX, pagina, total, margemTopo) {
    doc.addPage()
    prepararPagina(doc, dims, margemX, pagina, total)
    return margemTopo
}

function escreverTituloSlide(doc, titulo, y, margemX, larguraUtil) {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(16)
    doc.setTextColor(...COR_PRIMARIA)
    const linhas = doc.splitTextToSize(titulo || 'Slide', larguraUtil)
    doc.text(linhas, margemX, y)
    const fimTitulo = y + linhas.length * 7 + 2
    doc.setDrawColor(...COR_PRIMARIA_CLARA)
    doc.setLineWidth(0.6)
    doc.line(margemX, fimTitulo, margemX + Math.min(larguraUtil * 0.35, 55), fimTitulo)
    doc.setTextColor(0, 0, 0)
    return fimTitulo + 6
}

function calcularLargurasColunas(colunas, larguraTotal) {
    const pesos = colunas.map((c) => PESOS_COLUNA[c.campo] || PESOS_COLUNA.default)
    const soma = pesos.reduce((a, b) => a + b, 0)
    return pesos.map((p) => (p / soma) * larguraTotal)
}

function linhasCelula(doc, texto, larguraCol, fontSize) {
    doc.setFontSize(fontSize)
    return doc.splitTextToSize(String(texto || '—'), Math.max(larguraCol - 3, 8))
}

function renderizarCapa(doc, slide, dims, margemX) {
    const larguraUtil = dims.largura - margemX * 2
    let y = dims.altura * 0.4

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(26)
    doc.setTextColor(...COR_PRIMARIA)
    const titulo = doc.splitTextToSize(slide.titulo || 'Revisão Executiva de Prioridades', larguraUtil)
    doc.text(titulo, dims.largura / 2, y, { align: 'center' })
    y += titulo.length * 11 + 8

    doc.setDrawColor(...COR_PRIMARIA_CLARA)
    doc.setLineWidth(0.8)
    const linhaW = Math.min(80, larguraUtil * 0.4)
    doc.line(dims.largura / 2 - linhaW / 2, y, dims.largura / 2 + linhaW / 2, y)
    y += 14

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(14)
    doc.setTextColor(...COR_TEXTO_SECUNDARIO)
    const sub = slide.subtituloExibido || slide.subtitulo || ''
    if (sub) {
        doc.text(sub, dims.largura / 2, y, { align: 'center' })
    }
    doc.setTextColor(0, 0, 0)
}

function renderizarTexto(doc, slide, dims, margemX, margemTopo, larguraUtil, rodapeY, slideNum, totalSlides) {
    let y = escreverTituloSlide(doc, slide.titulo, margemTopo, margemX, larguraUtil)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(11)
    const bullets = slide.bullets || []
    if (!bullets.length) {
        doc.setTextColor(...COR_TEXTO_SECUNDARIO)
        doc.text('(sem conteúdo)', margemX + 2, y)
        doc.setTextColor(0, 0, 0)
        return
    }
    for (const b of bullets) {
        const linhas = doc.splitTextToSize(b, larguraUtil - 10)
        const bloco = linhas.length * 5.5 + 4
        if (y + bloco > rodapeY) {
            y = novaPagina(doc, dims, margemX, slideNum, totalSlides, margemTopo)
            y = escreverTituloSlide(doc, `${slide.titulo} (cont.)`, margemTopo, margemX, larguraUtil)
            doc.setFont('helvetica', 'normal')
            doc.setFontSize(11)
        }
        doc.setFillColor(...COR_PRIMARIA_CLARA)
        doc.circle(margemX + 2.5, y - 1.5, 0.8, 'F')
        doc.text(linhas, margemX + 7, y)
        y += bloco
    }
}

function renderizarTabela(doc, slide, dims, margemX, margemTopo, larguraUtil, rodapeY, slideNum, totalSlides) {
    let y = escreverTituloSlide(doc, slide.titulo, margemTopo, margemX, larguraUtil)

    const colunas = slide.colunas || []
    const linhas = slide.linhas || []

    if (!colunas.length || !linhas.length) {
        doc.setFont('helvetica', 'italic')
        doc.setFontSize(10)
        doc.setTextColor(...COR_TEXTO_SECUNDARIO)
        doc.text('Nenhum registro neste slide.', margemX + 2, y)
        doc.setTextColor(0, 0, 0)
        return
    }

    const nCols = colunas.length
    const colWidths = calcularLargurasColunas(colunas, larguraUtil)
    const fontSize = nCols > 5 ? 7.5 : 8.5
    const lineH = 4.2
    const padY = 2.5

    const desenharCabecalho = () => {
        const headerCells = colunas.map((col, c) => linhasCelula(doc, col.label, colWidths[c], fontSize))
        const maxHdrLinhas = Math.max(...headerCells.map((h) => h.length), 1)
        const headerH = maxHdrLinhas * lineH + padY * 2

        doc.setFillColor(...COR_PRIMARIA)
        doc.rect(margemX, y - padY, larguraUtil, headerH, 'F')
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(fontSize)
        doc.setTextColor(255, 255, 255)

        let x = margemX
        for (let c = 0; c < nCols; c++) {
            doc.text(headerCells[c], x + 2, y)
            x += colWidths[c]
        }
        y += headerH - padY
        doc.setTextColor(0, 0, 0)
        doc.setFont('helvetica', 'normal')
    }

    const desenharContinuacao = () => {
        y = novaPagina(doc, dims, margemX, slideNum, totalSlides, margemTopo)
        y = escreverTituloSlide(doc, `${slide.titulo} (cont.)`, margemTopo, margemX, larguraUtil)
    }

    desenharCabecalho()

    for (let i = 0; i < linhas.length; i++) {
        const celulas = colunas.map((col, c) => {
            const val = obterValorColuna(linhas[i], col.campo)
            return linhasCelula(doc, val, colWidths[c], fontSize)
        })
        const maxLinhas = Math.max(...celulas.map((l) => l.length), 1)
        const rowH = maxLinhas * lineH + padY * 2

        if (y + rowH > rodapeY) {
            desenharContinuacao()
            desenharCabecalho()
        }

        if (i % 2 === 1) {
            doc.setFillColor(...COR_ZEBRA)
            doc.rect(margemX, y - padY, larguraUtil, rowH, 'F')
        }

        doc.setDrawColor(220, 228, 228)
        doc.setLineWidth(0.15)
        doc.rect(margemX, y - padY, larguraUtil, rowH)

        let x = margemX
        for (let c = 0; c < nCols; c++) {
            doc.text(celulas[c], x + 2, y)
            if (c < nCols - 1) {
                doc.line(x + colWidths[c], y - padY, x + colWidths[c], y - padY + rowH)
            }
            x += colWidths[c]
        }
        y += rowH
    }
}

function renderizarImagem(doc, slide, dims, margemX, margemTopo, larguraUtil, rodapeY) {
    let y = escreverTituloSlide(doc, slide.titulo, margemTopo, margemX, larguraUtil)

    if (!slide.imagemDataUrl) {
        doc.setFont('helvetica', 'italic')
        doc.setFontSize(10)
        doc.setTextColor(...COR_TEXTO_SECUNDARIO)
        doc.text('Nenhuma imagem configurada.', margemX + 2, y)
        doc.setTextColor(0, 0, 0)
        return
    }

    try {
        const formato = slide.imagemDataUrl.includes('image/png') ? 'PNG'
            : slide.imagemDataUrl.includes('image/webp') ? 'WEBP'
                : 'JPEG'
        const maxW = larguraUtil
        const maxH = rodapeY - y - 14
        doc.addImage(slide.imagemDataUrl, formato, margemX, y, maxW, maxH, undefined, 'SLOW')
        y += maxH + 6
    } catch {
        doc.text('Não foi possível incluir a imagem.', margemX, y)
        y += 8
    }

    if (slide.imagemLegenda) {
        doc.setFont('helvetica', 'italic')
        doc.setFontSize(9)
        doc.setTextColor(...COR_TEXTO_SECUNDARIO)
        const leg = doc.splitTextToSize(slide.imagemLegenda, larguraUtil)
        doc.text(leg, margemX, Math.min(y, rodapeY))
        doc.setTextColor(0, 0, 0)
    }
}

/**
 * Gera e faz download do PDF da apresentação completa.
 * @param {Array} slidesResolvidos - saída de resolverSlidesParaExportacao
 */
export function gerarApresentacaoGestaoEntregasPdf(slidesResolvidos = []) {
    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' })
    const margemX = 14
    const margemTopo = 26
    const dims = dimensoesPagina(doc)
    const larguraUtil = dims.largura - margemX * 2
    const rodapeY = dims.altura - 14
    const total = slidesResolvidos.length || 1

    if (!slidesResolvidos.length) {
        prepararPagina(doc, dims, margemX, 1, 1)
        doc.setFont('helvetica', 'normal')
        doc.text('Apresentação vazia.', margemX, margemTopo)
        doc.save(nomeArquivoPdf())
        return
    }

    slidesResolvidos.forEach((slide, idx) => {
        const slideNum = idx + 1
        if (idx > 0) doc.addPage()
        prepararPagina(doc, dims, margemX, slideNum, total)

        switch (slide.tipo) {
            case 'capa':
                renderizarCapa(doc, slide, dims, margemX)
                break
            case 'texto':
                renderizarTexto(doc, slide, dims, margemX, margemTopo, larguraUtil, rodapeY, slideNum, total)
                break
            case 'tabela':
                renderizarTabela(doc, slide, dims, margemX, margemTopo, larguraUtil, rodapeY, slideNum, total)
                break
            case 'imagem':
                renderizarImagem(doc, slide, dims, margemX, margemTopo, larguraUtil, rodapeY)
                break
            default:
                escreverTituloSlide(doc, slide.titulo, margemTopo, margemX, larguraUtil)
        }
    })

    doc.save(nomeArquivoPdf())
}
